// src/utils/consult/localMedia.ts

export type MediaKind = "audio" | "video";

export const GET_USER_MEDIA_TIMEOUT_MS = 15_000;

export const isGetUserMediaTimeout = (err: unknown): boolean => (err as { name?: string } | null)?.name === "TimeoutError";

// A stream that arrives after the deadline is stopped, so a hung driver cannot leave a device held open.
export function getUserMediaWithTimeout(constraints: MediaStreamConstraints, ms: number = GET_USER_MEDIA_TIMEOUT_MS): Promise<MediaStream> {
	return new Promise((resolve, reject) => {
		const pending = navigator.mediaDevices.getUserMedia(constraints);
		let timedOut = false;
		const timer = setTimeout(() => {
			timedOut = true;
			const error = new Error("Camera/microphone access timed out. Please check your browser permissions.");
			error.name = "TimeoutError";
			reject(error);
		}, ms);
		pending.then(
			(stream) => {
				clearTimeout(timer);
				if (timedOut) stream.getTracks().forEach((track) => track.stop());
				else resolve(stream);
			},
			(err) => {
				clearTimeout(timer);
				reject(err);
			},
		);
	});
}

export interface ReplacementRequest {
	current: MediaStream | null;
	// A deviceId switches to that device (a live track already on it is kept), null opens the system default, undefined keeps the current track.
	cameraId?: string | null;
	micId?: string | null;
	videoConstraints?: MediaTrackConstraints;
	audioConstraints?: MediaTrackConstraints;
	timeoutMs?: number;
}

export interface Replacement {
	stream: MediaStream;
	changedAudio: boolean;
	changedVideo: boolean;
}

const planKind = (track: MediaStreamTrack | undefined, deviceId: string | null | undefined, constraints: MediaTrackConstraints = {}) => {
	if (deviceId === undefined) return { kept: track ?? null, request: false as const };
	if (deviceId !== null && track?.readyState === "live" && track.getSettings().deviceId === deviceId) return { kept: track, request: false as const };
	return { kept: null, request: deviceId === null ? { ...constraints } : { deviceId: { exact: deviceId }, ...constraints } };
};

// Resolves null when nothing needs opening. Some devices cannot open the same camera twice, so an unchanged kind keeps its live track.
export async function acquireReplacement(request: ReplacementRequest): Promise<Replacement | null> {
	const { current, cameraId, micId, videoConstraints, audioConstraints, timeoutMs } = request;
	const video = planKind(current?.getVideoTracks()[0], cameraId, videoConstraints);
	const audio = planKind(current?.getAudioTracks()[0], micId, audioConstraints);
	if (!video.request && !audio.request) return null;

	const acquired = await getUserMediaWithTimeout({ video: video.request || false, audio: audio.request || false }, timeoutMs);
	const stream = new MediaStream([...(audio.kept ? [audio.kept] : acquired.getAudioTracks()), ...(video.kept ? [video.kept] : acquired.getVideoTracks())]);
	return { stream, changedAudio: audio.request !== false, changedVideo: video.request !== false };
}

export type ForEachPeerConnection = (visit: (pc: RTCPeerConnection, peerId: string) => void) => void;

// replaceTrack needs no renegotiation; addTrack of a kind never sent renegotiates through the mesh's onnegotiationneeded.
export async function swapOntoPeers(forEachPc: ForEachPeerConnection, stream: MediaStream, options: { skipVideo?: boolean } = {}): Promise<boolean> {
	const swaps: Promise<void>[] = [];
	forEachPc((pc) => {
		const senders = pc.getSenders();
		stream.getTracks().forEach((track) => {
			if (options.skipVideo && track.kind === "video") return;
			const sender = senders.find((s) => s.track?.kind === track.kind);
			if (!sender) pc.addTrack(track, stream);
			else if (sender.track !== track) swaps.push(sender.replaceTrack(track));
		});
	});
	const results = await Promise.allSettled(swaps);
	return results.every((result) => result.status === "fulfilled");
}

// Stops every track of `oldStream` that `newStream` does not carry; called the other way round it discards a stale replacement.
export function releaseReplaced(oldStream: MediaStream | null | undefined, newStream: MediaStream | null | undefined): void {
	const kept = newStream?.getTracks() ?? [];
	oldStream?.getTracks().forEach((track) => {
		if (!kept.includes(track)) track.stop();
	});
}

// A still-listed selection wins, then the device actually open, then the first listed.
export function seedDeviceSelection(devices: ReadonlyArray<{ deviceId: string }>, inUseTrack?: MediaStreamTrack | null, selected?: string): string {
	if (selected && devices.some((device) => device.deviceId === selected)) return selected;
	const inUse = inUseTrack?.getSettings().deviceId;
	return inUse && devices.some((device) => device.deviceId === inUse) ? inUse : (devices[0]?.deviceId ?? "");
}

export interface InputDevices {
	cameras: MediaDeviceInfo[];
	microphones: MediaDeviceInfo[];
}

export async function listInputDevices(): Promise<InputDevices> {
	const devices = await navigator.mediaDevices.enumerateDevices();
	return {
		cameras: devices.filter((device) => device.kind === "videoinput"),
		microphones: devices.filter((device) => device.kind === "audioinput"),
	};
}

export function watchDeviceList(onChange: () => void): () => void {
	const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
	if (!mediaDevices || typeof mediaDevices.addEventListener !== "function") return () => {};
	mediaDevices.addEventListener("devicechange", onChange);
	return () => mediaDevices.removeEventListener("devicechange", onChange);
}

// Deferred a microtask so a caller that stops a track and drops its stream in the same task can tell its own stop() from a lost device.
export function watchTrackEnded(stream: MediaStream, onEnded: (track: MediaStreamTrack) => void): () => void {
	let active = true;
	const detach = stream.getTracks().map((track) => {
		const listener = () =>
			queueMicrotask(() => {
				if (active) onEnded(track);
			});
		track.addEventListener("ended", listener);
		return () => track.removeEventListener("ended", listener);
	});
	return () => {
		active = false;
		detach.forEach((off) => off());
	};
}
